import type { ComponentActionType } from "@v1/types/component-action-type";
import type { EditorCanvas } from "replo-utils/lib/misc/canvas";

import * as React from "react";

import * as coreActions from "@v1/actions/core-actions";
import { selectActiveCanvas } from "@v1/components/canvas/canvas-reducer";
import { useEditorDispatch, useEditorStore } from "@v1/store/store";

/**
 * NOTE (Chance 2024-06-14): Not every action receives the `activeCanvas`, but
 * many do, so we can pass it along to every action as an implicit dependency to
 * simplify use in editor components. No harm in passing it so long as other
 * actions don't take `activeCanvas` as some other type.
 */
export type UseApplyComponentActionType<
  T extends ComponentActionType = ComponentActionType,
> = T extends { type: "applyCompositeAction" }
  ? T extends { activeCanvas: any }
    ? Omit<T, "value" | "activeCanvas"> & {
        value: UseApplyComponentActionType[];
        activeCanvas?: never;
      }
    : Omit<T, "value"> & { value: UseApplyComponentActionType[] }
  : T extends { activeCanvas: any }
    ? Omit<T, "activeCanvas"> & { activeCanvas?: never }
    : T;

function useApplyComponentAction() {
  const dispatch = useEditorDispatch();
  const store = useEditorStore();

  const applyComponentAction = React.useCallback(
    (action: UseApplyComponentActionType<ComponentActionType>) => {
      const activeCanvas = selectActiveCanvas(store.getState());
      return dispatch(
        coreActions.applyComponentAction(
          forwardCanvasToAction(action, activeCanvas),
        ),
      );
    },
    [dispatch, store],
  );

  return applyComponentAction;
}

export default useApplyComponentAction;

function forwardCanvasToAction(
  action: UseApplyComponentActionType | ComponentActionType,
  activeCanvas: EditorCanvas,
): ComponentActionType {
  if (action.type === "applyCompositeAction") {
    const childActions = action.value;
    return {
      ...action,
      activeCanvas,
      value: childActions.map((childAction) => {
        return forwardCanvasToAction(childAction, activeCanvas);
      }),
    };
  }
  // Note (Evan, 2024-07-09): If an action specifies an active canvas, keep it.
  return { activeCanvas, ...action } as ComponentActionType;
}
